import { memo, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import 'swiper/css'
import api from '@/core/services/api'

/**
 * 메인 배너 — gold8.bet 처럼 화면 폭 전체, 자동 넘김.
 *   PC: 그룹 1, 높이 320px(1920×320 배너를 왼쪽부터 깔고 좁은 화면에선 오른쪽이 잘림).
 *   모바일: 그룹 2, 화면 폭에 맞춰 600:160 비율(m.gold8.bet 600×160).
 *   그룹 번호는 유저 API 가 사이트 안 순서(1·2…)로 내려준다.
 *   banners 를 안 넘기면(모바일) 직접 /site/banner 를 부른다.
 */
const Root = styled.div`
  width: 100%;
  /* 모바일 메인은 세로 flex 안에 있어서, 최소 폭을 0 으로 두지 않으면 Swiper 가 폭을 끝없이 키운다 */
  min-width: 0;
  max-width: 100vw;
  overflow: hidden;
  background: #000;
  ${({ $mobile }) => ($mobile ? 'aspect-ratio: 600 / 160;' : 'height: 320px;')}
  .swiper { width: 100%; }
  .swiper, .swiper-wrapper, .swiper-slide { height: 100%; }
`

const Slide = styled.div`
  width: 100%;
  height: 100%;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* gold: 배너를 왼쪽부터 깔고, 화면이 좁으면 오른쪽이 잘린다 */
    object-position: left center;
  }
`

const Empty = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #121c26, #1a242d, #121c26);
`

function GoldBanner({ banners, loading = false, mobile = false }) {
  const navigate = useNavigate()
  const group = mobile ? 2 : 1
  const [own, setOwn] = useState(null)

  useEffect(() => {
    if (banners !== undefined) return
    api.get('/site/banner').then((res) => setOwn(res.data?.content || [])).catch(() => setOwn([]))
  }, [banners])

  const source = banners !== undefined ? banners : own
  const list = useMemo(
    () => (source || []).filter(b => (b.group_no ?? b.type) === group),
    [source, group],
  )
  const wait = (list[0]?.wait_time ?? 4) * 1000

  // 같은 사이트 링크는 화면 안에서 이동(모바일은 게임 화면 경로를 /m 쪽으로), 바깥 링크만 새 창
  const go = (link) => {
    const url = (link || '').trim()
    if (!url || url === '/') return
    try {
      const u = new URL(url, window.location.origin)
      if (u.origin === window.location.origin) {
        let path = u.pathname + u.search + u.hash
        if (mobile && path.startsWith('/game-play/')) path = '/m' + path
        navigate(path)
        return
      }
    } catch (e) { /* 주소로 못 읽으면 새 창 */ }
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  if (loading || source === null || !list.length) {
    return <Root $mobile={mobile}><Empty /></Root>
  }

  return (
    <Root $mobile={mobile}>
      <Swiper
        modules={[Autoplay]}
        loop={list.length > 1}
        autoplay={list.length > 1 ? { delay: wait, disableOnInteraction: false } : false}
        speed={600}
      >
        {list.map((b, i) => (
          <SwiperSlide key={b.no || b.img_uuid || i}>
            <Slide $clickable={!!(b.link && b.link.trim() && b.link.trim() !== '/')} onClick={() => go(b.link)}>
              <img
                src={`${process.env.REACT_APP_IMG_URL}${b.img_uuid}`}
                alt=""
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            </Slide>
          </SwiperSlide>
        ))}
      </Swiper>
    </Root>
  )
}

export default memo(GoldBanner)
